import {produce} from 'immer';

import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Helpers from '../helpers/helpers';
import type * as Types from '../types';
import type {PricingGroupOption} from '../types';
import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';

const transformItemToSummaryPeriodItem = ({
    item,
    selectedPricingGroup,
    periodPricingGroups,
    summaryPricingGroups,
}: {
    item: Types.Tuition | Types.Fee | Types.DiscountAmount;
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    periodPricingGroups: null | Array<Types.PeriodPricingGroup>;
    summaryPricingGroups: Types.SummaryPricingGroups;
}): Types.SummaryPeriodItem => {
    const filteredPricingGroups = Helpers.filterPricingGroupsBySelectedPricingGroup({
        selectedPricingGroup,
        allPricingGroups: item.pricingGroups,
        periodPricingGroups,
        summaryPricingGroups,
    });
    const sortedPricingGroups = Helpers.sortPricingGroups(filteredPricingGroups, summaryPricingGroups);
    const pricingGroupsWithChildren = sortedPricingGroups.map(pricingGroup => {
        const children =
            periodPricingGroups
                ?.find(periodPricingGroup => periodPricingGroup.pricingGroupId === pricingGroup.id)
                ?.children.map(({fullName}) => fullName) ?? null;
        return {
            ...pricingGroup,
            children:
                hasValue(selectedPricingGroup) && selectedPricingGroup !== AUTOMATIC_PRICING_GROUP_ID ? null : children,
        };
    });

    return {
        name: item.title,
        pricingGroups: pricingGroupsWithChildren,
    };
};

const getSummaryPricingGroups = (
    pricingGroupIds: Array<string>,
    pricingGroupOptions: Array<PricingGroupOption>,
): Types.SummaryPricingGroups => {
    return pricingGroupIds.reduce<Types.SummaryPricingGroups>(
        produce((acc, current, index) => {
            const pricingGroupOption = pricingGroupOptions.find(option => option.value === current);
            if (!hasValue(pricingGroupOption)) {
                return acc;
            }
            if (!acc[current]) {
                acc[current] = {name: pricingGroupOption.label, order: index};
                return acc;
            }

            return acc;
        }),
        {},
    );
};

const transformPriceForBiweeklySummaryPeriod = <T extends Types.Tuition | Types.Fee>(tuitonsOrFees: T[]): T[] => {
    return tuitonsOrFees.map(tuitonOrFee => ({
        ...tuitonOrFee,
        pricingGroups: tuitonOrFee.pricingGroups.map(pricingGroup => ({
            ...pricingGroup,
            price: pricingGroup.price * 2,
        })),
    }));
};

export {transformItemToSummaryPeriodItem, getSummaryPricingGroups, transformPriceForBiweeklySummaryPeriod};
